import { memo, useMemo } from 'react';
import type { FC } from 'react';
import './EmptyGraph.css';
import { Logo } from '@ipfabric/ui-components/Logo/Logo';
import { isGraphSharedViewParameters } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { CompareSnapshotEmptyContent } from 'components/Graph/EmptyGraph/CompareSnapshotEmptyContent';
import { DefaultEmptyContent } from 'components/Graph/EmptyGraph/DefaultEmptyContent';
import { FailedPathlookup } from 'components/Graph/EmptyGraph/FailedPathlookup';
import { IntentVerificationEmptyContent } from 'components/Graph/EmptyGraph/IntentVerificationEmptyContent';
import { NetworkViewerEmptyContent } from 'components/Graph/EmptyGraph/NetworkViewerEmptyContent';
import { NetworkViewerNoDevices } from 'components/Graph/EmptyGraph/NetworkViewerNoDevices';
import { PathLookupEmptyContent } from 'components/Graph/EmptyGraph/PathLookupEmptyContent';
import { SharedViewEmptyContent } from 'components/Graph/EmptyGraph/SharedViewEmptyContent';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphTopControls } from 'components/Graph/GraphTopControls';
import { finishRender } from 'components/Graph/commands/rendering/finishRender';
import { selectGraphMode } from 'components/Graph/model/selectors/graphMetadata/selectGraphMode';
import { TipOfTheDay } from 'components/TipOfTheDay/TipOfTheDay';
import { useLocale } from 'containers/App/LocaleContext';
import { pipe } from 'fp-ts/function';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { getRandomInteger } from 'utils/random/getRandomInteger';
import { useRotatingNumber } from 'utils/react/useRotatingNumber';
import { areSharedViewSnapshotsLoaded } from '../helpers/areSharedViewSnapshotsLoaded';
import { EmptyGraphReason } from './EmptyGraphReason';
import { SharedViewNotLoadedSnapshot } from './SharedViewNotLoadedSnapshot';

const TIP_ROTATING_INTERVAL = 10000;

const BareEmptyGraph: FC = () => {
  const {
    canvasGraph: { emptyGraph: msgEmptyGraph },
  } = useLocale();
  const snapshots = useSnapshots();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    firstLoading: prop('firstLoading'),
    graphMode: selectGraphMode,
    initialParameters: prop('initialParameters'),
    longRendering: prop('longRendering'),
    sidePanel: prop('sidePanel'),
    sharedViewId: prop('sharedViewId'),
    emptyGraphReason: prop('emptyGraphReason'),
  });

  const webGlEnabled = useMemo(() => {
    const gl = document.createElement('canvas').getContext('webgl');
    return gl !== null;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies(ctxProps.sharedViewId): recheck on view id change
  const sharedViewNotLoadedSnapshot = useMemo(() => {
    if (isGraphSharedViewParameters(ctxProps.initialParameters)) {
      return !areSharedViewSnapshotsLoaded(
        ctxProps.initialParameters,
        snapshots,
      );
    }
    return false;
  }, [ctxProps.initialParameters, ctxProps.sharedViewId, snapshots]);

  const tipsOfTheDayToShow = useMemo(
    () =>
      pipe(msgEmptyGraph.tipsOfTheDay, (tips) =>
        webGlEnabled ? tips : [...tips, msgEmptyGraph.tipsOfTheDayWebGl],
      ),
    [msgEmptyGraph, webGlEnabled],
  );

  const tipOfTheDayIndex = useRotatingNumber({
    initialNumber: () => getRandomInteger(tipsOfTheDayToShow.length),
    interval: TIP_ROTATING_INTERVAL,
    limit: tipsOfTheDayToShow.length,
  });

  if (ctxProps.longRendering) {
    ctxProps.dispatch(finishRender);
  }

  if (ctxProps.firstLoading) {
    // don't show content before load of first content,
    // because it looks weird when this placeholder appears behind initial loader
    // show empty div to reserve space and push toolbar to right
    return <div className='flex flex-1' />;
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable, big switch
  const renderContent = () => {
    if (sharedViewNotLoadedSnapshot) {
      return <SharedViewNotLoadedSnapshot />;
    }
    if (ctxProps.emptyGraphReason) {
      return <EmptyGraphReason />;
    }
    if (ctxProps.sharedViewId) {
      return <SharedViewEmptyContent />;
    }
    if (ctxProps.graphMode === 'pathLookup') {
      return <FailedPathlookup />;
    }
    if (ctxProps.graphMode === 'topology') {
      return <NetworkViewerNoDevices />;
    }
    if (
      ctxProps.sidePanel.open &&
      ctxProps.sidePanel.contentId === 'networkViewer'
    ) {
      return <NetworkViewerEmptyContent />;
    }
    if (
      ctxProps.sidePanel.open &&
      ctxProps.sidePanel.contentId === 'pathlookup'
    ) {
      return <PathLookupEmptyContent />;
    }
    if (
      ctxProps.sidePanel.open &&
      ctxProps.sidePanel.contentId === 'intentVerification'
    ) {
      return <IntentVerificationEmptyContent />;
    }
    if (
      ctxProps.sidePanel.open &&
      ctxProps.sidePanel.contentId === 'compareSnapshot'
    ) {
      return <CompareSnapshotEmptyContent />;
    }

    return <DefaultEmptyContent />;
  };

  return (
    <div className='empty-graph relative flex flex-1 flex-col items-center justify-center'>
      <GraphTopControls />
      <div className='flex flex-1 flex-col justify-center px-4'>
        <div className='empty-graph__content text-lg'>
          <Logo color='shade' className='h-auto max-w-full' />
          <hr className='mb-lgu-14 mt-10' />
          {renderContent()}
        </div>
      </div>
      <div className='empty-graph__tip-of-the-day-container'>
        <TipOfTheDay>{tipsOfTheDayToShow[tipOfTheDayIndex]}</TipOfTheDay>
      </div>
    </div>
  );
};

export const EmptyGraph = memo(BareEmptyGraph);
